import './styles/base/normalize.css';
import './styles/global.css';
import './styles/themes/light.css';
import './styles/themes/dark.css';

import { useEffect, lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { Header, Footer } from './components/layout';
import { SettingsButton, useSettings } from './features/settings';
import { ErrorBoundary } from './components/common';
import { OfflineIndicator } from './components/common/OfflineIndicator';
import { BookmarksProvider } from './hooks/useBookmarks';
import { AnalyticsProvider } from './components/common/AnalyticsProvider';
import ReloadPrompt from './components/common/ReloadPrompt';
import ReadingProgressBar from './components/common/ReadingProgressIndicator/ReadingProgressBar';
import { useProductTour } from './features/product-tour';

// Lazy load route components
const Landing = lazy(() => import('./pages/Landing'));
const Bookmarks = lazy(() => import('./pages/Bookmarks'));
const ReadingHistory = lazy(() => import('./pages/ReadingHistory'));
const Contributors = lazy(() => import('./pages/Contributors'));
const Books = lazy(() => import('./pages/Books'));
const Locations = lazy(() => import('./pages/Locations'));
const DevelopmentHelp = lazy(() => import('./features/development'));
const SuttaTitle = lazy(() =>
  import('./features/sutta-title').then(m => ({ default: m.SuttaTitle }))
);
const InteractiveDhammaList = lazy(() =>
  import('./pages/InteractiveDhamma').then(m => ({ default: m.InteractiveDhammaList }))
);
const InteractiveDhammaViewer = lazy(() => import('./pages/InteractiveDhamma'));

function App() {
  const { theme, fontSize } = useSettings();

  useEffect(() => {
    // Apply theme class to document element
    const themeClass = `theme-${theme}`;
    document.documentElement.className = theme === 'dark' ? `${themeClass} dark` : themeClass;
    document.documentElement.setAttribute('data-theme', theme);

    // Apply theme class to body
    document.body.className = theme === 'dark' ? `${themeClass} dark` : themeClass;

    // Set font size CSS variable
    document.documentElement.style.setProperty('--base-font-size', `${fontSize}px`);
  }, [theme, fontSize]);

  // Hide the initial loader on mount (prevents CLS - loader is fixed overlay)
  useEffect(() => {
    const loader = document.querySelector('.initial-loader');
    if (loader) {
      loader.classList.add('hidden');
      // Remove from DOM after fade out
      setTimeout(() => loader.remove(), 300);
    }
  }, []);

  // Product Tour - trigger on first visit (works on all pages)
  const { startTour } = useProductTour();

  useEffect(() => {
    // Only trigger if user hasn't seen the tour
    // Check localStorage directly to avoid stale closure
    const seen = localStorage.getItem('kt_tour_seen_v1') === 'true';
    if (!seen) {
      // Delay to allow page to render fully
      const timer = setTimeout(() => {
        startTour();
      }, 2000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  return (
    <HelmetProvider>
      <BookmarksProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <div className="App">
              <ReadingProgressBar />
              <Header />
              <main id="main-content" style={{ minHeight: '100vh' }}>
                <AnalyticsProvider>
                  <Suspense
                    fallback={
                      <div
                        className="hero-section"
                        style={{
                          minHeight: '100vh',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          padding: '2rem',
                          background:
                            'radial-gradient(circle at center, rgba(139, 69, 19, 0.05) 0%, transparent 70%)',
                        }}
                      >
                        <h1 className="hero-title">
                          <span className="block text-4xl mb-2 opacity-90 text-amber-900 dark:text-amber-100">
                            ನಮೋ ಬುದ್ಧಾಯ
                          </span>
                          <span className="block text-6xl md:text-8xl font-bold my-4 bg-clip-text text-transparent bg-gradient-to-r from-amber-600 to-yellow-500 dark:from-amber-200 dark:to-yellow-400 filter drop-shadow-lg">
                            ತಿಪಿಟಕ
                          </span>
                        </h1>
                        <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
                          ಪಾಲಿಸಾಹಿತ್ಯದಲ್ಲಿ ಸಂರಕ್ಷಿತವಾಗಿರುವ ಬುದ್ಧವಚನಗಳ ಕನ್ನಡ ಅನುವಾದ
                        </p>
                      </div>
                    }
                  >
                    <Routes>
                      <Route path="/" element={<Landing />} />
                      <Route path="/bookmarks" element={<Bookmarks />} />
                      <Route path="/reading-history" element={<ReadingHistory />} />
                      <Route path="/contributors" element={<Contributors />} />
                      <Route path="/books" element={<Books />} />
                      <Route path="/locations" element={<Locations />} />
                      <Route path="/interactive-dhamma" element={<InteractiveDhammaList />} />
                      <Route
                        path="/interactive-dhamma/:slug"
                        element={<InteractiveDhammaViewer />}
                      />
                      <Route path="/dev" element={<DevelopmentHelp />} />
                      <Route path="/beta/:id" element={<SuttaTitle />} />
                      <Route path="/:id/:subid?/:section?" element={<SuttaTitle />} />
                    </Routes>
                  </Suspense>
                </AnalyticsProvider>
              </main>
              <Footer />
              <SettingsButton />
              <OfflineIndicator />
              <ReloadPrompt />
            </div>
          </BrowserRouter>
        </ErrorBoundary>
      </BookmarksProvider>
    </HelmetProvider>
  );
}

export default App;
